import { apiRequest } from '@/utils/api-request';

interface CotAvailabilityApiResponse {
  session_ids_with_cot: string[];
}

/**
 * Bulk-check which of the given session IDs have at least one persisted
 * CoT event (thinking / agent_message / tool_call / tool_result).
 *
 * Executes a single OpenSearch aggregation on the backend, so cost is
 * constant regardless of list length.
 *
 * Fail-open: returns null on any network or server error so callers
 * leave their state unchanged and keep export buttons enabled rather
 * than incorrectly disabling them.  Returns an empty Set only when the
 * backend explicitly confirms no CoT exists for the supplied sessions.
 *
 * @param orgId - Organization ID
 * @param workflowId - Workflow ID (used for auth scoping on the backend)
 * @param sessionIds - Session IDs to check; nulls/empties are filtered out
 * @returns Set of session IDs that have CoT events, or null on error
 */
export async function getRunsCotAvailability(
  orgId: string,
  workflowId: string,
  sessionIds: (string | null | undefined)[],
): Promise<Set<string> | null> {
  const filtered = sessionIds.filter((id): id is string => Boolean(id));
  if (!filtered.length) return null;

  try {
    const response = await apiRequest<CotAvailabilityApiResponse>({
      url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/runs/cot-availability`,
      method: 'POST',
      target: 'workflow',
      body: { session_ids: filtered },
    });
    const data = (response.data ?? response) as CotAvailabilityApiResponse;
    if (!Array.isArray(data?.session_ids_with_cot)) return null;
    return new Set(data.session_ids_with_cot);
  } catch {
    return null;
  }
}
